import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useList } from '@yingbaba/api';
import { ActionDialog, QueryState, Pagination } from '@yingbaba/ui';
import { datetime, money } from '@yingbaba/utils';
import type { Campaign, CampaignReport } from '@yingbaba/types';

export function CampaignResults({ campaign, write }: { campaign: Campaign; write: boolean }) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const q = useList<CampaignReport>(
    `/campaigns/${campaign.id}/reports?page=${page}&page_size=10`,
    open,
  );
  const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
  return (
    <div className="stack" style={{ marginTop: 14 }}>
      {campaign.source_project_id && (
        <Link className="link small" to={`/projects/${campaign.source_project_id}?tab=files`}>
          查看项目素材
        </Link>
      )}
      <p className="small muted">
        效果数据由组织按渠道后台记录，按累计口径填写；每次统计单独保存。
      </p>
      {write && (
        <ActionDialog
          title="更正发布链接"
          trigger="更正链接"
          variant="secondary"
          path={`/campaigns/${campaign.id}/correct-link`}
          submitLabel="保存更正记录"
          fields={[
            { name: 'url', label: '实际发布链接', type: 'url', value: campaign.published_url },
            { name: 'reason', label: '更正说明', type: 'textarea', min: 5, max: 500 },
          ]}
        />
      )}
      {write && (
        <ActionDialog
          title="记录推广效果"
          trigger="记录效果"
          variant="secondary"
          path={`/campaigns/${campaign.id}/reports`}
          idempotent
          submitLabel="保存本次统计"
          onSuccess={() => {
            setPage(1);
            setOpen(true);
          }}
          fields={[
            { name: 'measured_at', label: '数据统计时间', type: 'datetime-local', value: localNow },
            {
              name: 'views',
              label: '累计播放 / 浏览数',
              type: 'number',
              min: 0,
              step: 1,
              value: '0',
            },
            { name: 'likes', label: '累计点赞数', type: 'number', min: 0, step: 1, value: '0' },
            { name: 'leads', label: '已确认咨询数', type: 'number', min: 0, step: 1, value: '0' },
            {
              name: 'cost',
              label: '累计投放支出（元）',
              type: 'number',
              min: 0,
              step: 0.01,
              value: '0',
            },
            { name: 'source_url', label: '渠道数据来源链接（可选）', type: 'url', required: false },
            {
              name: 'note',
              label: '统计口径与来源说明',
              type: 'textarea',
              min: 5,
              max: 2000,
              placeholder: '例如：截至今日的视频号后台累计数据；咨询数按已核实的作品留言统计。',
            },
          ]}
          transform={(v) => ({
            ...v,
            measured_at: new Date(v.measured_at).toISOString(),
            views: Number(v.views),
            likes: Number(v.likes),
            leads: Number(v.leads),
            cost_minor: Math.round(Number(v.cost) * 100),
          })}
        />
      )}
      <details onToggle={(e) => setOpen(e.currentTarget.open)} open={open}>
        <summary>查看效果记录</summary>
        {open && (
          <QueryState query={q} empty={q.data?.total === 0}>
            <div className="stack" style={{ marginTop: 16 }}>
              {q.data?.items.map((report) => (
                <div key={report.id} className="stack">
                  <strong className="small">截至 {datetime(report.measured_at)}</strong>
                  <p className="small">
                    播放 {report.views.toLocaleString()} · 点赞 {report.likes.toLocaleString()} ·
                    咨询 {report.leads.toLocaleString()} · 支出 {money(report.cost_minor)}
                  </p>
                  <p className="small prose muted">{report.note}</p>
                  {report.source_url && (
                    <a
                      className="link small"
                      href={report.source_url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      核查数据来源
                    </a>
                  )}
                </div>
              ))}
            </div>
            <Pagination
              page={page}
              size={q.data?.page_size || 10}
              total={q.data?.total || 0}
              onPage={setPage}
            />
          </QueryState>
        )}
      </details>
    </div>
  );
}
